import React from 'react';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { WorkflowNavigation } from 'Organisms/WorkflowNavigation';
import { MetaResourceRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRenderer';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import {
  FDBodyText, FDDisplayText, FDHeadlineText, FDLabelText,
} from '@facet-wealth/design';
import { Richtext } from 'Packages/FacetDesign/Components/Richtext';
import Image from 'Context/AnnouncementContext/Dialog/DialogContent/Image';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import { PromoCodeInputContextProvider } from 'Context/PromoCodeInputContext';
import { stripeNonSecretKey } from 'Consts/keys';
import { StripeProvider } from 'react-stripe-elements';
import StripeInput from './StripeInput/StripeInput';
import EstatePlanningRecommendationBuyStepLayout from './Layout';
import {
  PaymentMethod,
} from './PaymentMethod';

const estatePlanningMetaOverrideSet = buildFacetDesignOverride()
  .nameOverride(['title'], {
    _component: () => FDHeadlineText,
    _customProps: { size: 'lg' },
  }, overrideReadOnly)
  .nameOverride(['paymentMethodText'], {
    _component: () => FDHeadlineText,
  }, overrideReadOnly)
  .nameOverride(['partnerText', 'recurringPaymentText', 'packageDetailText'], {
    _component: () => FDBodyText,
    _customProps: { size: 'sm' },
  }, overrideReadOnly)
  .nameOverride(['firstYearAmountWithDiscount'], {
    _component: () => Richtext,
    _customProps: { color: 'textSecondary', Component: FDDisplayText },
  }, overrideReadOnly)
  .nameOverride(['firstYearAmount', 'recurringPaymentAmount'], {
    _component: () => FDDisplayText,
    _customProps: { color: 'textSecondary' },
  }, overrideReadOnly)
  .nameOverride(['dueTodayText'], {
    _component: () => FDBodyText,
  }, overrideReadOnly)
  .nameOverride(['packageText'], {
    _component: () => FDBodyText,
    _customProps: { color: 'textSecondary' },
  }, overrideReadOnly)
  .nameOverride(['disclaimerText'], {
    _component: () => FDLabelText,
    _customProps: { size: 'lg' },
  }, overrideReadOnly)
  .nameOverride('partnerImageURL', {
    _component: () => Image,
    _customProps: { noBorderRadius: true },
  }, overrideReadOnly)
  .nameOverride('stripeToken', {
    _component: () => StripeInput,
  }, overrideReadOnly)
  .nameOverride('firstYearText', {
    _component: () => FDBodyText,
    _customProps: { size: 'lg' },
  }, overrideReadOnly)
  .nameOverride('paymentMethod', {
    _component: () => PaymentMethod,
  }, overrideReadOnly);

const taxFilingPurchaseMetaOverrideSet = buildFacetDesignOverride()
  .nameOverride(['title'], {
    _component: () => FDHeadlineText,
    _customProps: { size: 'lg' },
  }, overrideReadOnly)
  .nameOverride(['paymentMethodText'], {
    _component: () => FDHeadlineText,
  }, overrideReadOnly)
  .nameOverride(['partnerText', 'recurringPaymentText', 'packageDetailText'], {
    _component: () => FDBodyText,
    _customProps: { size: 'sm' },
  }, overrideReadOnly)
  .nameOverride(['firstYearAmount', 'firstYearAmountWithDiscount'], {
    _component: () => Richtext,
    _customProps: { Component: FDHeadlineText, size: 'lg' },
  }, overrideReadOnly)
  .nameOverride(['recurringPaymentAmount'], {
    _component: () => FDDisplayText,
    _customProps: { color: 'textSecondary' },
  }, overrideReadOnly)
  .nameOverride(['dueTodayText'], {
    _component: () => FDBodyText,
  }, overrideReadOnly)
  .nameOverride(['packageText'], {
    _component: () => FDBodyText,
    _customProps: { color: 'textSecondary' },
  }, overrideReadOnly)
  .nameOverride(['disclaimerText'], {
    _component: () => FDLabelText,
    _customProps: { size: 'lg' },
  }, overrideReadOnly)
  .nameOverride('partnerImageURL', {
    _component: () => Image,
    _customProps: { noBorderRadius: true },
  }, overrideReadOnly)
  .nameOverride('stripeToken', {
    _component: () => StripeInput,
  }, overrideReadOnly)
  .nameOverride('firstYearText', {
    _component: () => FDBodyText,
    _customProps: { size: 'lg' },
  }, overrideReadOnly)
  .nameOverride('paymentMethod', {
    _component: () => PaymentMethod,
  }, overrideReadOnly);


const Template = ({ metaOverrideSet }) => {
  const { pageContentResource } = usePageContentContext();
  const { setPageContentCollection } = usePageContentContext();
  useBoneBackgroundColor();

  return (
    <>
      <WorkflowNavigation resource={pageContentResource} />
      <StripeProvider apiKey={stripeNonSecretKey}>
        <PromoCodeInputContextProvider>
          <MetaResourceRenderer
            resource={pageContentResource}
            layout={EstatePlanningRecommendationBuyStepLayout}
            metaOverrideSet={metaOverrideSet}
            onSubmit={setPageContentCollection}
          />
        </PromoCodeInputContextProvider>
      </StripeProvider>
    </>
  );
};

export const TaxFilingPurchaseTemplate = () => {
  return Template({ metaOverrideSet: taxFilingPurchaseMetaOverrideSet });
};

export const EstatePlanningRecommendationBuyStepTemplate = () => {
  return Template({ metaOverrideSet: estatePlanningMetaOverrideSet });
};
